import { Box, Button, Typography } from '@mui/material'
import { type ReactNode, useState } from 'react'
import { ApiError } from '@/lib/api'
import { ehErroDeRede, type MensagensPorErro, mensagemDeErro, tituloDeErro } from '@/lib/erros'
import { cn } from '@/lib/utils'
import NiExclamationSquare from '@/ui/icons/nexture/ni-exclamation-square'
import NiPlug from '@/ui/icons/nexture/ni-plug'

export interface FailureProps {
  error: unknown
  /**
   * Sem isso o botão de tentar de novo não aparece. Se devolver promessa, o
   * botão fica ocupado até ela assentar.
   */
  onRetry?: (() => unknown) | undefined
  /** Troca o título que sai do tipo de erro. */
  title?: string | undefined
  /** Texto próprio da tela por código ou status. Ver `lib/erros.ts`. */
  contexto?: MensagensPorErro | undefined
  /** Outra saída além de tentar de novo, como voltar para a lista. */
  action?: ReactNode | undefined
  /** Para dentro de painel e gaveta: em linha, sem a altura de tela vazia. */
  compact?: boolean | undefined
  className?: string | undefined
}

/**
 * Estado de erro.
 *
 * Título curto pelo tipo de erro e a frase de `mensagemDeErro`, que diz o que
 * aconteceu e o que fazer. A etiqueta técnica fica num `title`, para quando a
 * pessoa precisar dizer o que apareceu. Nada de código de erro grande na tela.
 */
export function Failure({
  error,
  onRetry,
  title,
  contexto,
  action,
  compact,
  className,
}: FailureProps) {
  const [tentando, setTentando] = useState(false)
  const code = error instanceof ApiError ? error.code : undefined
  const Icon = ehErroDeRede(error) ? NiPlug : NiExclamationSquare

  function tentar(): void {
    if (onRetry === undefined || tentando) return
    const resultado = onRetry()
    if (resultado instanceof Promise) {
      setTentando(true)
      // O erro da nova tentativa volta pela própria query; aqui só se solta o botão.
      void resultado.catch(() => undefined).finally(() => setTentando(false))
    }
  }

  const botoes = (onRetry !== undefined || action !== undefined) && (
    <Box className={cn('flex flex-wrap gap-2', compact ? 'mt-2' : 'mt-4 justify-center')}>
      {onRetry !== undefined && (
        <Button
          variant="outlined"
          color="grey"
          size="small"
          onClick={tentar}
          loading={tentando}
          className="min-h-11"
        >
          Tentar de novo
        </Button>
      )}
      {action}
    </Box>
  )

  if (compact) {
    return (
      <Box
        data-slot="failure"
        role="alert"
        className={cn('flex flex-row items-start gap-3 py-3', className)}
      >
        <Icon size={20} className="text-error-dark mt-0.5 flex-none" aria-hidden />
        <Box className="min-w-0">
          <Typography
            variant="body2"
            component="p"
            className="text-text-primary font-medium"
            title={code}
          >
            {title ?? tituloDeErro(error)}
          </Typography>
          <Typography variant="body2" className="text-text-primary-light">
            {mensagemDeErro(error, contexto)}
          </Typography>
          {botoes}
        </Box>
      </Box>
    )
  }

  return (
    <Box
      data-slot="failure"
      role="alert"
      className={cn('flex flex-col items-center justify-center px-6 py-12 text-center', className)}
    >
      <Icon size={28} className="text-error-dark mb-3" aria-hidden />
      <Box className="max-w-sm">
        <Typography variant="subtitle1" component="p" className="text-text-primary" title={code}>
          {title ?? tituloDeErro(error)}
        </Typography>
        <Typography variant="body2" className="text-text-primary-light mt-1">
          {mensagemDeErro(error, contexto)}
        </Typography>
        {botoes}
      </Box>
    </Box>
  )
}
